<script setup>
// 主題形象蓋章：左邊是「我的數位章」（四種主題章與今日剩餘枚數），右邊是步道列表，
// 把章拖到步道的蓋章欄位上，全部擺好再按最下面的「送出蓋章」才寫入。
//
// 資料取自 /front/stamp（未登入也回得到版面，配額照一枚都沒用回）；步道一次全部帶回，
// 分區篩選與搜尋在前台做，拖到一半換分區不必等後端。
// 送出走 POST /front/stamp，成功後直接拿回傳的最新配額覆蓋畫面，不重打一次。
//
// 拖曳用 Pointer Events 自己做而不是 HTML5 drag and drop：後者在手機上根本不會觸發，
// 滑鼠與手指要共用同一套就只能這樣。放開時以 elementFromPoint 找底下的步道卡。
// 另外每個空的蓋章欄位本身也是按鈕，點一下就蓋上那一格的主題章——拖不動的裝置與鍵盤都用得了。
import bannerBg from '~/assets/img/rules-banner-bg.png'
import searchIcon from '~/assets/img/icon-search.png'
import doneArt from '~/assets/img/trail-vote-done.png'
import wheelArt from '~/assets/img/account-chance-wheel.png'
import banner from '~/assets/img/banner/banner3.png'
import { STAMP_SUBMIT_ENABLED } from '~/utils/voteSwitch'

definePageMeta({ layout: false })

const config = useRuntimeConfig()
useHead({ title: config.public.siteName })

const api = useApi()
const { resolveFileUrl } = useFileUrl()
const { isLogged } = useAuth()
const { openLogin } = useLoginDialog()
const { ensureProfileComplete, goCompleteProfile, isProfileIncomplete } = useProfileGate()
const { lockScroll } = useScrollLock()

// 四種主題章的外觀：名稱與順序以後端為準（utils/voteConst.js 的 STAMP_TYPES），
// 這裡只管顏色與圖示。圖示是 24×24 的線條 SVG，顏色跟著 currentColor 走
const STAMP_LOOKS = {
  elder: {
    color: '#eb7128',
    soft: '#fff1e6',
    paths: [
      'M9 2.3a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z',
      'M9.5 8 8.5 14.5 6.5 21',
      'M8.5 14.5 11 17.5 11.5 21',
      'M9.3 9.6 12.5 12h3',
      'M15.5 12v9'
    ]
  },
  family: {
    color: '#2f86d6',
    soft: '#e8f3fd',
    paths: [
      'M6.5 3.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z',
      'M17.5 3.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4Z',
      'M12 11a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2Z',
      'M3.5 21v-6.5a3 3 0 0 1 6 0V21',
      'M14.5 21v-6.5a3 3 0 0 1 6 0V21',
      'M10 21v-3a2 2 0 0 1 4 0v3'
    ]
  },
  culture: {
    color: '#8a4fd1',
    soft: '#f3ecfd',
    paths: [
      'M3 9.5 12 4l9 5.5',
      'M4 9.5h16',
      'M6 12.5v5.5',
      'M10 12.5v5.5',
      'M14 12.5v5.5',
      'M18 12.5v5.5',
      'M3.5 20.5h17'
    ]
  },
  nature: {
    color: '#2f9e5b',
    soft: '#e6f6ec',
    paths: [
      'M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.5 19 2c1 2 2 4.2 2 8 0 5.5-4.8 10-10 10Z',
      'M2 21c0-3 1.9-5.4 5.1-6C9.5 14.5 12 13 13 12'
    ]
  }
}

// 介面掛掉時照設計稿的四種主題渲染，左邊那一欄才不會整塊消失
const FALLBACK_TYPES = [
  { stamp_key: 'elder', stamp_name: '全齡友善' },
  { stamp_key: 'family', stamp_name: '親子共融' },
  { stamp_key: 'culture', stamp_name: '人文古蹟' },
  { stamp_key: 'nature', stamp_name: '自然生態' }
].map((t) => ({ ...t, total: 10, used: 0, remaining: 10 }))

const TIPS = [
  '不同主題的數位章可蓋在不同步道，發掘更多步道的特色！',
  '同一主題的數位章，同一條步道每日最多蓋 1 枚。',
  '一條步道每日最多可獲得 4 種不同主題章。',
  '當日的數位章當日有效，未蓋完的不累積至隔日。'
]

const { data, refresh } = await useAsyncData('stamp-board', () =>
  api.get('/front/stamp').catch(() => null)
)

// 登入彈窗走的是整頁跳轉，但 token 換發失敗被登出這種情況會在頁面上直接變，重抓一次才對得上
watch(isLogged, () => refresh())

/* ---------------- 配額 ---------------- */

// 送出成功後由介面回傳的最新配額覆蓋，不必重打一次
const quota = ref(null)
watch(
  data,
  (d) =>
    (quota.value = d
      ? { types: d.types, stamped: d.stamped || {}, bonus: d.bonus, date: d.date }
      : null),
  { immediate: true }
)

const types = computed(() => (quota.value?.types?.length ? quota.value.types : FALLBACK_TYPES))
const typeName = (key) => types.value.find((t) => t.stamp_key === key)?.stamp_name || ''
const bonus = computed(() => quota.value?.bonus || { available: false, claimed: false, times: 1 })

// 還沒送出的章：步道 id → 主題章陣列。只存在前端，按「送出蓋章」才寫進後端
const pending = ref({})

const pendingList = computed(() =>
  Object.entries(pending.value).flatMap(([trailId, keys]) =>
    keys.map((key) => ({ trail_id: Number(trailId), stamp_key: key }))
  )
)
const pendingCount = computed(() => pendingList.value.length)

function pendingOf(key) {
  return pendingList.value.filter((p) => p.stamp_key === key).length
}

// 左邊徽章上的 ×N：今天剩下的減掉已經擺上去、還沒送出的
function remainingOf(key) {
  const t = types.value.find((x) => x.stamp_key === key)
  return Math.max(0, (t?.remaining ?? 0) - pendingOf(key))
}

// 一格蓋章欄位的狀態：done 今天已送出、pending 擺上去還沒送出、empty 空著
function slotState(trailId, key) {
  if ((quota.value?.stamped?.[trailId] || []).includes(key)) return 'done'
  if ((pending.value[trailId] || []).includes(key)) return 'pending'
  return 'empty'
}

/* ---------------- 還沒送出的章：跨頁保留 ---------------- */

// 送出前會被導去登入或會員中心補資料，回來時擺好的章還在才不會白拖一場。
// 只留在這個分頁（sessionStorage），也只認同一天，隔天配額已經重算，舊的擺法沒有意義
const PENDING_KEY = 'stamp_pending'

onMounted(() => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null')
    if (!saved || saved.date !== quota.value?.date) return
    const next = {}
    for (const [trailId, keys] of Object.entries(saved.items || {})) {
      if (!trailMap.value.has(Number(trailId))) continue
      const ok = keys.filter((k) => slotState(Number(trailId), k) === 'empty' && remainingOf(k) > 0)
      if (ok.length) next[trailId] = ok
    }
    pending.value = next
  } catch {
    // 讀不到就當作沒有，版面照常
  }
})

watch(
  pending,
  (items) => {
    try {
      if (!Object.keys(items).length) return sessionStorage.removeItem(PENDING_KEY)
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ date: quota.value?.date, items }))
    } catch {
      // 無痕模式寫不進去：只是回來時要重拖，不影響送出
    }
  },
  { deep: true }
)

/* ---------------- 步道列表 ---------------- */

const zones = computed(() => data.value?.zones || [])
const zone = ref('')
const keyword = ref('')

const trails = computed(() =>
  (data.value?.trails || []).map((t) => ({
    id: t.id,
    name: t.trail_name,
    zoneKey: t.zone_key,
    zoneName: t.zone_name,
    area: [t.city, t.district].filter(Boolean).join(''),
    cover: resolveFileUrl(t.cover)
  }))
)
const trailMap = computed(() => new Map(trails.value.map((t) => [t.id, t])))

const visibleTrails = computed(() => {
  const kw = keyword.value.trim()
  return trails.value.filter(
    (t) => (!zone.value || t.zoneKey === zone.value) && (!kw || t.name.includes(kw))
  )
})

/* ---------------- 蓋章 ---------------- */

const toast = ref('')
let toastTimer = null
function say(text) {
  toast.value = text
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => (toast.value = ''), 2400)
}

// 剛蓋上去的那一格播一次「蓋章」動畫；key 帶時間戳，同一格拿掉再蓋也會重播
const justPlaced = ref('')

/**
 * 把一枚章擺到步道上（還沒送出）
 * @returns {boolean} 擺得上去與否，擺不上去會自己提示原因
 */
function place(trailId, key) {
  if (!isLogged.value) {
    openLogin()
    return false
  }
  const trail = trailMap.value.get(trailId)
  if (!trail) return false
  const state = slotState(trailId, key)
  if (state === 'done') {
    say(`今天已經在「${trail.name}」蓋過${typeName(key)}了`)
    return false
  }
  if (state === 'pending') {
    say(`「${trail.name}」已經擺上${typeName(key)}了`)
    return false
  }
  if (remainingOf(key) <= 0) {
    say(`今天的${typeName(key)}已經用完了，明天再來蓋吧`)
    return false
  }
  pending.value = { ...pending.value, [trailId]: [...(pending.value[trailId] || []), key] }
  justPlaced.value = `${trailId}:${key}:${Date.now()}`
  return true
}

function unplace(trailId, key) {
  const keys = (pending.value[trailId] || []).filter((k) => k !== key)
  const next = { ...pending.value }
  if (keys.length) next[trailId] = keys
  else delete next[trailId]
  pending.value = next
}

function onSlot(trailId, key) {
  const state = slotState(trailId, key)
  if (state === 'pending') return unplace(trailId, key)
  if (state === 'done') return say('今天已經蓋過這一格了，送出後就不能再改')
  place(trailId, key)
}

function clearPending() {
  pending.value = {}
}

const isPlaced = (trailId, key) => justPlaced.value.startsWith(`${trailId}:${key}:`)

/* ---------------- 拖曳 ---------------- */

const listEl = ref(null)
// 拖曳中的狀態：key 主題章、x/y 指標位置、over 底下那張步道卡的 id
const drag = ref(null)
let dragStart = null
let scrollSpeed = 0
let scrollRaf = 0

// 指標靠近列表上下緣就自動捲動，否則拖著章找不到畫面外的步道
const EDGE = 64

function hitTrail(x, y) {
  const el = document.elementFromPoint(x, y)?.closest('[data-trail-id]')
  return el ? Number(el.dataset.trailId) : null
}

function autoScroll() {
  if (!drag.value || !scrollSpeed || !listEl.value) {
    scrollRaf = 0
    return
  }
  listEl.value.scrollTop += scrollSpeed
  // 捲動時指標沒動，底下的卡片卻換了，要跟著重找一次
  drag.value.over = hitTrail(drag.value.x, drag.value.y)
  scrollRaf = requestAnimationFrame(autoScroll)
}

function onPointerMove(e) {
  if (!drag.value) return
  e.preventDefault()
  const { clientX: x, clientY: y } = e
  // 按下去抖一下不算拖，否則單純點一下徽章也會被當成拖到空處
  if (!drag.value.moved && Math.hypot(x - dragStart.x, y - dragStart.y) < 6) return
  drag.value = { ...drag.value, x, y, moved: true, over: hitTrail(x, y) }

  const rect = listEl.value?.getBoundingClientRect()
  scrollSpeed = 0
  if (rect && x >= rect.left && x <= rect.right) {
    if (y < rect.top + EDGE) scrollSpeed = -Math.ceil((rect.top + EDGE - y) / 6)
    else if (y > rect.bottom - EDGE) scrollSpeed = Math.ceil((y - (rect.bottom - EDGE)) / 6)
  }
  if (scrollSpeed && !scrollRaf) scrollRaf = requestAnimationFrame(autoScroll)
}

function endDrag(e) {
  const d = drag.value
  stopDrag()
  if (!d) return
  if (!d.moved) return say('按住數位章拖到步道上，或直接點步道下方的蓋章欄位')
  const over = e?.type === 'pointerup' ? hitTrail(e.clientX, e.clientY) : null
  if (over) place(over, d.key)
}

function stopDrag() {
  drag.value = null
  scrollSpeed = 0
  cancelAnimationFrame(scrollRaf)
  scrollRaf = 0
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', endDrag)
  window.removeEventListener('pointercancel', endDrag)
}

function onBadgeDown(e, key) {
  // 只認滑鼠左鍵；手指與觸控筆沒有 button 的分別
  if (e.pointerType === 'mouse' && e.button !== 0) return
  if (!isLogged.value) return openLogin()
  if (remainingOf(key) <= 0) return say(`今天的${typeName(key)}已經用完了，明天再來蓋吧`)
  e.preventDefault()
  dragStart = { x: e.clientX, y: e.clientY }
  drag.value = { key, x: e.clientX, y: e.clientY, moved: false, over: null }
  window.addEventListener('pointermove', onPointerMove, { passive: false })
  window.addEventListener('pointerup', endDrag)
  window.addEventListener('pointercancel', endDrag)
}

// 拖到某張卡上時，那張卡要讓人一眼看出放得下（橘框）還是放不下（灰掉）
function dropHint(trailId) {
  if (!drag.value?.moved || drag.value.over !== trailId) return ''
  return slotState(trailId, drag.value.key) === 'empty' ? 'is-over' : 'is-blocked'
}

/* ---------------- 送出 ---------------- */

const submitting = ref(false)
const dialog = ref(null)

async function onSubmit() {
  if (submitting.value || !pendingCount.value) return
  if (!isLogged.value) return openLogin()
  // 個人資料沒填齊先去會員中心補，擺好的章存在 sessionStorage，補完回來還在
  if (!(await ensureProfileComplete())) return

  submitting.value = true
  try {
    const res = await api.post('/front/stamp', { stamps: pendingList.value })
    quota.value = { types: res.types, stamped: res.stamped, bonus: res.bonus, date: res.date }
    pending.value = {}
    // 一天只有第一次送出送抽獎機會；之後的送出只跳一般的成功提示，不再說送了機會
    dialog.value = res.draw_granted
      ? {
          art: wheelArt,
          title: '蓋章成功，獲得抽獎機會！',
          text: `已送出 ${res.stamped_count} 枚數位章，恭喜獲得`,
          strong: `${res.draw_times} 次抽獎機會`,
          note: '每日完成蓋章送出可獲得 1 次，明天記得再來蓋章！'
        }
      : {
          art: doneArt,
          title: '蓋章成功',
          text: `已送出 ${res.stamped_count} 枚數位章，感謝你對步道的支持！`,
          strong: '',
          note: ''
        }
  } catch (err) {
    if (isProfileIncomplete(err)) return goCompleteProfile()
    dialog.value = { art: '', title: '', text: err.message, strong: '', note: '' }
  } finally {
    submitting.value = false
  }
}

function closeDialog() {
  dialog.value = null
}

function onKeydown(e) {
  if (e.key === 'Escape') closeDialog()
}

watch(dialog, (open) => {
  if (open) {
    window.addEventListener('keydown', onKeydown)
    lockScroll(true)
    return
  }
  window.removeEventListener('keydown', onKeydown)
  lockScroll(false)
})

onBeforeUnmount(() => {
  stopDrag()
  clearTimeout(toastTimer)
  window.removeEventListener('keydown', onKeydown)
})

const bonusText = computed(() => {
  if (!bonus.value.available) return '目前非票選期間，蓋章照樣計入排行，但不送抽獎機會'
  if (bonus.value.claimed) return '今日的抽獎機會已領取，明天送出蓋章可再獲得 1 次'
  return `今日完成蓋章並送出，即可獲得 ${bonus.value.times} 次抽獎機會`
})
</script>

<template>
  <div class="stamp" :class="{ 'is-dragging': drag?.moved }">
    <div class="head-fix">
      <SiteHeader active="stamp" />
    </div>

    <!-- 橫幅：底圖與活動辦法同一張，標題牌與說明條也沿用同一套手作感 -->
    <!--
    <section class="banner" :style="{ backgroundImage: `url(${bannerBg})` }">
      <div class="hero">
        <h1 class="plaque">主題形象蓋章</h1>
        <p class="sub">用數位章，為你喜愛的步道蓋上支持！</p>
        <ol class="steps">
          <li><b>1</b>選擇數位章</li>
          <li><b>2</b>拖曳至步道的蓋章欄位</li>
          <li><b>3</b>按下送出蓋章</li>
        </ol>
      </div>
    </section>
    -->
    <section>
      <img
        style="width: 100%; height: auto; display: block"
        :src="banner"
        alt=""
        aria-hidden="true"
      />
    </section>

    <main class="body">
      <div class="wrap">
        <div class="board">
          <!-- 左：我的數位章 -->
          <aside class="dock" aria-label="我的數位章">
            <h2 class="dock-title">
              <span class="dock-title-full">我的數位章</span>
              <span class="dock-title-short">數位章</span>
            </h2>
            <p class="dock-date">每日各 10 枚，當日有效</p>

            <ul class="badges">
              <li v-for="t in types" :key="t.stamp_key">
                <button
                  class="badge"
                  :class="{ 'is-empty': remainingOf(t.stamp_key) <= 0 }"
                  type="button"
                  :style="{
                    '--c': STAMP_LOOKS[t.stamp_key]?.color,
                    '--soft': STAMP_LOOKS[t.stamp_key]?.soft
                  }"
                  :aria-label="`${t.stamp_name}，今日剩 ${remainingOf(t.stamp_key)} 枚，按住拖曳到步道上`"
                  @pointerdown="onBadgeDown($event, t.stamp_key)"
                >
                  <span class="badge-ico">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path v-for="(d, i) in STAMP_LOOKS[t.stamp_key]?.paths" :key="i" :d="d" />
                    </svg>
                  </span>
                  <span class="badge-name">{{ t.stamp_name }}</span>
                  <span class="badge-count">×{{ remainingOf(t.stamp_key) }}</span>
                </button>
              </li>
            </ul>

            <div class="bonus" :class="{ 'is-off': !bonus.available || bonus.claimed }">
              {{ bonusText }}
            </div>

            <div class="tips">
              <h3>小提醒</h3>
              <ul>
                <li v-for="tip in TIPS" :key="tip">{{ tip }}</li>
              </ul>
              <NuxtLink class="tips-link" to="/rules?tab=stamp">查看完整蓋章規則</NuxtLink>
            </div>
          </aside>

          <!-- 右：步道列表 -->
          <section class="panel" aria-label="步道列表">
            <div class="panel-head">
              <h2 class="panel-title">步道列表</h2>
              <label class="search">
                <img class="search-ico" :src="searchIcon" alt="" aria-hidden="true" />
                <input
                  v-model="keyword"
                  class="search-input"
                  type="search"
                  placeholder="搜尋步道名稱"
                  aria-label="搜尋步道名稱"
                />
              </label>
            </div>

            <div class="chips" role="tablist" aria-label="步道分區">
              <button
                class="chip"
                :class="{ 'is-on': !zone }"
                type="button"
                role="tab"
                :aria-selected="!zone"
                @click="zone = ''"
              >
                全部
              </button>
              <button
                v-for="z in zones"
                :key="z.zone_key"
                class="chip"
                :class="{ 'is-on': zone === z.zone_key }"
                type="button"
                role="tab"
                :aria-selected="zone === z.zone_key"
                @click="zone = z.zone_key"
              >
                {{ z.zone_name }}
              </button>
            </div>

            <div ref="listEl" class="list">
              <ul v-if="visibleTrails.length" class="cards">
                <li
                  v-for="(t, idx) in visibleTrails"
                  :key="t.id"
                  class="card"
                  :class="dropHint(t.id)"
                  :data-trail-id="t.id"
                >
                  <div class="cover">
                    <img
                      v-if="t.cover"
                      :src="t.cover"
                      :alt="t.name"
                      loading="lazy"
                      draggable="false"
                    />
                    <span v-else class="cover-empty" aria-hidden="true"></span>
                    <span class="cover-no">{{ idx + 1 }}</span>
                  </div>

                  <div class="info">
                    <h3 class="name">
                      <NuxtLink :to="`/trails/${t.id}`">{{ t.name }}</NuxtLink>
                    </h3>
                    <p class="area">{{ [t.zoneName, t.area].filter(Boolean).join('・') }}</p>

                    <ul class="slots">
                      <li v-for="type in types" :key="type.stamp_key">
                        <button
                          class="slot"
                          :class="[
                            `is-${slotState(t.id, type.stamp_key)}`,
                            {
                              'is-target':
                                drag?.moved && drag.over === t.id && drag.key === type.stamp_key,
                              'is-placed': isPlaced(t.id, type.stamp_key)
                            }
                          ]"
                          type="button"
                          :style="{
                            '--c': STAMP_LOOKS[type.stamp_key]?.color,
                            '--soft': STAMP_LOOKS[type.stamp_key]?.soft
                          }"
                          :title="
                            slotState(t.id, type.stamp_key) === 'pending'
                              ? `取消${type.stamp_name}`
                              : type.stamp_name
                          "
                          :aria-label="`${t.name}的${type.stamp_name}欄位：${
                            {
                              done: '今天已蓋',
                              pending: '已擺上，點一下取消',
                              empty: '點一下蓋章'
                            }[slotState(t.id, type.stamp_key)]
                          }`"
                          @click="onSlot(t.id, type.stamp_key)"
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path
                              v-for="(d, i) in STAMP_LOOKS[type.stamp_key]?.paths"
                              :key="i"
                              :d="d"
                            />
                          </svg>
                          <span
                            v-if="slotState(t.id, type.stamp_key) === 'pending'"
                            class="slot-x"
                            aria-hidden="true"
                            >×</span
                          >
                        </button>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
              <p v-else class="empty">
                {{ data ? '沒有符合條件的步道' : '步道資料載入失敗，請稍後再試' }}
              </p>
            </div>

            <div class="submit-bar">
              <!-- 手機版左欄太窄放不下抽獎說明，改印在送出鈕上方 -->
              <p class="submit-bonus">{{ bonusText }}</p>
              <p class="submit-info">
                已擺上 <b>{{ pendingCount }}</b> 枚
                <button
                  v-if="pendingCount"
                  class="clear-btn"
                  type="button"
                  :disabled="submitting"
                  @click="clearPending"
                >
                  全部清除
                </button>
              </p>
              <button
                v-if="STAMP_SUBMIT_ENABLED"
                class="submit-btn"
                type="button"
                :disabled="!pendingCount || submitting"
                @click="onSubmit"
              >
                {{ submitting ? '送出中…' : '送出蓋章' }}
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>

    <SiteFooter />

    <ClientOnly>
      <Teleport to="body">
        <!-- 拖曳中跟著指標走的那枚章；pointer-events: none，底下的卡片才找得到 -->
        <div
          v-if="drag?.moved"
          class="stamp-ghost"
          :style="{
            left: `${drag.x}px`,
            top: `${drag.y}px`,
            '--c': STAMP_LOOKS[drag.key]?.color,
            '--soft': STAMP_LOOKS[drag.key]?.soft
          }"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24">
            <path v-for="(d, i) in STAMP_LOOKS[drag.key]?.paths" :key="i" :d="d" />
          </svg>
        </div>

        <Transition name="toast">
          <p v-if="toast" class="stamp-toast" role="status">{{ toast }}</p>
        </Transition>

        <Transition name="dlg">
          <div v-if="dialog" class="dlg-mask" @click.self="closeDialog()">
            <div class="dlg-panel" role="alertdialog" aria-modal="true" aria-labelledby="dlg-text">
              <span v-if="dialog.art" class="dlg-art">
                <img :src="dialog.art" alt="" aria-hidden="true" />
              </span>
              <h2 v-if="dialog.title" class="dlg-title">{{ dialog.title }}</h2>
              <p id="dlg-text" class="dlg-text">
                {{ dialog.text }}<b v-if="dialog.strong">{{ dialog.strong }}</b>
              </p>
              <p v-if="dialog.note" class="dlg-note">{{ dialog.note }}</p>
              <button class="dlg-btn" type="button" @click="closeDialog()">確認</button>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style scoped>
.stamp {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: var(--site-font);
  color: var(--site-ink);
  background: #fff;
}

/* 拖曳中整頁不選字，否則拖過去一路反白 */
.stamp.is-dragging {
  user-select: none;
  cursor: grabbing;
}

.head-fix {
  position: sticky;
  top: 0;
  z-index: 50;
}

/* ---------------- 橫幅 ---------------- */

.banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
}

.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}

/* 白色標題牌與藍色說明條帶一點傾斜，與活動辦法的橫幅同一套手作感 */
.plaque {
  margin: 0;
  padding: 12px 40px;
  background: #fff;
  color: #101010;
  font-size: clamp(40px, 5vw, 68px);
  font-weight: 900;
  line-height: 1.1;
  transform: rotate(-1deg);
  box-shadow: 0 6px 16px rgba(var(--site-shadow-rgb), 0.18);
}

.sub {
  margin: 0;
  padding: 6px 18px;
  background: #60d3f7;
  color: #ffd84a;
  font-size: clamp(16px, 1.6vw, 22px);
  font-weight: 700;
  line-height: 1.3;
  transform: rotate(-1deg);
  text-shadow:
    -2px 0 #111,
    2px 0 #111,
    0 -2px #111,
    0 2px #111,
    -2px -2px #111,
    2px -2px #111,
    -2px 2px #111,
    2px 2px #111;
}

.steps {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
}

.steps li {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px 0 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 4px 12px rgba(var(--site-shadow-rgb), 0.12);
  color: var(--site-title);
  font-size: 15px;
  font-weight: 700;
}

.steps b {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--site-orange);
  color: #fff;
  font-size: 14px;
}

/* ---------------- 內容區 ---------------- */

.body {
  position: relative;
  flex: 1;
  padding: 40px 0 calc(min(12.15vw, 175px) + 40px);
  background: linear-gradient(180deg, #f4ffdb 0, #fdfff7 480px, #ffffff 900px);
}

.wrap {
  max-width: var(--site-max);
  margin: 0 auto;
  padding: 0 20px;
}

/* 左右兩欄的高度鎖在一個畫面內：步道在右欄裡捲，送出鈕永遠在看得到的最下面，
   拖著章就不必跟整頁的捲動搶 */
.board {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 24px;
  height: calc(100vh - var(--site-header-h) - 48px);
  height: calc(100dvh - var(--site-header-h) - 48px);
  min-height: 600px;
  max-height: 900px;
}

/* ---------------- 我的數位章 ---------------- */

.dock {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 24px 20px;
  border: 1px solid #f2e7d6;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(var(--site-shadow-rgb), 0.06);
  overflow-y: auto;
}

.dock-title {
  margin: 0;
  color: var(--site-title);
  font-size: 22px;
  font-weight: 900;
  line-height: 1.2;
}

.dock-title-short {
  display: none;
}

.dock-date {
  margin: 4px 0 0;
  color: var(--site-text-weak);
  font-size: 13px;
}

.badges {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

/* 徽章本身就是拖曳的把手：touch-action: none 讓手指按住時不會變成捲動頁面 */
.badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 14px 6px 12px;
  border: 1px solid transparent;
  border-radius: 18px;
  background: var(--soft);
  color: var(--c);
  font-family: inherit;
  cursor: grab;
  touch-action: none;
  user-select: none;
  transition:
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1),
    box-shadow 0.25s,
    border-color 0.25s;
}

.badge:hover:not(.is-empty) {
  border-color: var(--c);
  box-shadow: 0 8px 18px rgba(var(--site-shadow-rgb), 0.12);
  transform: translateY(-2px);
}

.badge:active:not(.is-empty) {
  cursor: grabbing;
}

.badge.is-empty {
  filter: grayscale(1);
  opacity: 0.5;
  cursor: not-allowed;
}

.badge-ico {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border: 3px solid var(--c);
  border-radius: 50%;
  background: #fff;
}

.badge-ico svg,
.slot svg,
.stamp-ghost svg {
  width: 58%;
  height: 58%;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.badge-name {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.badge-count {
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
}

.bonus {
  margin-top: 16px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(var(--site-orange-rgb), 0.1);
  color: var(--site-orange-deep);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
}

.bonus.is-off {
  background: #f4f4f4;
  color: var(--site-text-sub);
  font-weight: 500;
}

.tips {
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fbfff1;
  border: 1px dashed #cfe8a6;
}

.tips h3 {
  margin: 0 0 6px;
  color: #3d7a1f;
  font-size: 15px;
  font-weight: 900;
}

.tips ul {
  margin: 0;
  padding-left: 18px;
  color: var(--site-text);
  font-size: 13px;
  line-height: 1.6;
}

.tips-link {
  display: inline-block;
  margin-top: 8px;
  color: var(--site-orange);
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* ---------------- 步道列表 ---------------- */

.panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border: 1px solid #f2e7d6;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(var(--site-shadow-rgb), 0.06);
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 0;
}

.panel-title {
  margin: 0;
  color: var(--site-title);
  font-size: 22px;
  font-weight: 900;
  line-height: 1.2;
  white-space: nowrap;
}

.search {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid #99999999;
  border-radius: 999px;
  background: #fff;
  transition: border-color 0.25s;
}

.search:focus-within {
  border-color: var(--site-orange-soft);
}

.search-ico {
  flex: none;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: var(--site-ink);
  font-family: inherit;
  font-size: 14px;
  outline: none;
}

.search-input::placeholder {
  color: #b3b3b3;
}

.search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}

.chips {
  display: flex;
  gap: 8px;
  padding: 14px 24px 12px;
  overflow-x: auto;
}

.chip {
  flex: none;
  height: 36px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: #f6f6f6;
  color: #202020;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background-color 0.25s,
    color 0.25s;
}

.chip:hover {
  color: var(--site-orange);
}

.chip.is-on {
  background: var(--site-orange);
  color: #fff;
}

.list {
  flex: 1;
  min-height: 0;
  padding: 4px 24px 20px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 18px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.card {
  display: flex;
  flex-direction: column;
  border: 2px solid #f1ede6;
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s,
    opacity 0.2s;
}

/* 拖到上面：放得下是橘框微微浮起，放不下（這種章今天已經蓋過）就淡掉 */
.card.is-over {
  border-color: var(--site-orange);
  box-shadow: 0 10px 24px rgba(var(--site-orange-rgb), 0.22);
  transform: translateY(-2px);
}

.card.is-blocked {
  opacity: 0.55;
}

.cover {
  position: relative;
  /* 比投票頁的卡片扁：這一頁的主角是底下的蓋章欄位，一屏要多看得到幾條步道 */
  aspect-ratio: 2 / 1;
  overflow: hidden;
}

.cover img,
.cover-empty {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.cover-empty {
  background: linear-gradient(165deg, #cfe3c2 0%, #8fb98a 45%, #4f7f6a 100%);
}

/* 設計稿卡片左上角的綠色流水號 */
.cover-no {
  position: absolute;
  left: 12px;
  top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 999px;
  background: #2e6b3f;
  color: #fff;
  font-size: 14px;
  font-weight: 900;
}

.info {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 12px 14px 14px;
}

.name {
  margin: 0;
  color: #02050a;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.name a {
  transition: color 0.25s;
}

.name a:hover {
  color: var(--site-orange);
}

.area {
  min-height: 20px;
  margin: 2px 0 0;
  color: var(--site-text-weak);
  font-size: 13px;
}

.slots {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: auto 0 0;
  padding: 12px 0 0;
  list-style: none;
}

/* 四格蓋章欄位，每格固定一種主題：空的是虛線圈 + 淡淡的主題圖示，
   看得出這一格該蓋哪一種；蓋上去就換成實心的主題章 */
.slot {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 2px dashed #d6d6d6;
  border-radius: 50%;
  background: #fafafa;
  color: var(--c);
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s,
    transform 0.2s;
}

.slot.is-empty svg {
  opacity: 0.28;
}

.slot.is-empty:hover,
.slot.is-target {
  border-color: var(--c);
  background: var(--soft);
  transform: scale(1.08);
}

.slot.is-empty:hover svg,
.slot.is-target svg {
  opacity: 0.8;
}

.slot.is-pending,
.slot.is-done {
  border: 3px solid var(--c);
  background: var(--soft);
}

/* 今天已經送出的章不能再改，用一圈淡色標出「定案了」 */
.slot.is-done {
  cursor: default;
  box-shadow: 0 0 0 3px #fff inset;
}

.slot.is-placed {
  animation: stamp-in 0.42s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.slot-x {
  position: absolute;
  right: -5px;
  top: -5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #444;
  color: #fff;
  font-size: 13px;
  line-height: 1;
}

@keyframes stamp-in {
  0% {
    transform: scale(1.7) rotate(-14deg);
    opacity: 0;
  }
  60% {
    transform: scale(0.9) rotate(3deg);
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(0);
  }
}

.empty {
  margin: 60px 0;
  color: var(--site-text-weak);
  font-size: 15px;
  text-align: center;
}

/* ---------------- 送出列 ---------------- */

.submit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 24px;
  border-top: 1px solid #f2e7d6;
  background: #fffaf3;
}

.submit-bonus {
  display: none;
}

.submit-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: var(--site-text-sub);
  font-size: 15px;
}

.submit-info b {
  color: var(--site-orange-bright);
  font-size: 24px;
  font-weight: 900;
}

.clear-btn {
  padding: 0;
  border: 0;
  background: none;
  color: var(--site-text-weak);
  font-family: inherit;
  font-size: 13px;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.submit-btn {
  flex: none;
  min-width: 200px;
  height: 52px;
  padding: 0 28px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-family: inherit;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background-color 0.25s,
    box-shadow 0.25s,
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.submit-btn:hover:not(:disabled) {
  background: var(--site-orange-deep);
  box-shadow: 0 10px 22px rgba(var(--site-orange-rgb), 0.3);
  transform: translateY(-2px);
}

.submit-btn:disabled {
  background: #f7b98c;
  cursor: not-allowed;
}

/* ---------------- 彈窗（與投票成功同一套） ---------------- */

.dlg-mask {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
}

.dlg-panel {
  width: 100%;
  max-width: 360px;
  padding: 36px 24px 32px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(var(--site-shadow-rgb), 0.28);
  text-align: center;
}

.dlg-art {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  height: 130px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: #faf5ec;
  overflow: hidden;
}

.dlg-art img {
  display: block;
  width: 112px;
  height: 112px;
  object-fit: contain;
}

.dlg-title {
  margin: 0;
  color: #111111;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
}

.dlg-text {
  margin: 12px 0 0;
  color: #333333;
  font-size: 15px;
  line-height: 1.6;
}

.dlg-text b {
  color: var(--site-orange);
  font-weight: 700;
}

.dlg-note {
  margin: 8px 0 0;
  color: var(--site-text-weak);
  font-size: 13px;
  line-height: 1.5;
}

.dlg-btn {
  width: 100%;
  height: 56px;
  margin-top: 26px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.25s;
}

.dlg-btn:hover {
  background: var(--site-orange-deep);
}

.dlg-enter-active,
.dlg-leave-active {
  transition: opacity 0.25s ease;
}

.dlg-enter-active .dlg-panel,
.dlg-leave-active .dlg-panel {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dlg-enter-from,
.dlg-leave-to {
  opacity: 0;
}

.dlg-enter-from .dlg-panel,
.dlg-leave-to .dlg-panel {
  transform: translateY(16px) scale(0.96);
}

/* ---------------- 拖曳中的章與提示 ---------------- */

/* Teleport 到 body 的節點吃不到 scoped 的祖先選擇器，但自己的 class 仍帶 scope，照常生效 */
.stamp-ghost {
  position: fixed;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: -32px 0 0 -32px;
  border: 3px solid var(--c);
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 12px 28px rgba(var(--site-shadow-rgb), 0.28);
  color: var(--c);
  pointer-events: none;
  transform: rotate(-8deg) scale(1.05);
}

.stamp-toast {
  position: fixed;
  left: 50%;
  bottom: 32px;
  z-index: 250;
  max-width: calc(100vw - 32px);
  margin: 0;
  padding: 12px 20px;
  border-radius: 999px;
  background: rgba(17, 17, 17, 0.86);
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
  transform: translateX(-50%);
  pointer-events: none;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

/* ---------------- 窄螢幕 ---------------- */

@media (max-width: 1024px) {
  .board {
    grid-template-columns: 240px minmax(0, 1fr);
  }

  .badges {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .badge-ico {
    width: 52px;
    height: 52px;
  }
}

/* 手機：照設計稿收成「左邊一條窄窄的數位章 + 右邊步道列表」，
   步道卡改成左圖右文的橫條，四格欄位縮小排在名稱底下 */
@media (max-width: 767px) {
  .banner {
    min-height: 0;
    padding: 36px 16px;
  }

  .plaque {
    padding: 10px 24px;
  }

  .steps li {
    height: 34px;
    font-size: 13px;
  }

  .steps b {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  .body {
    padding-top: 20px;
  }

  .wrap {
    padding: 0 12px;
  }

  .board {
    grid-template-columns: 84px minmax(0, 1fr);
    gap: 10px;
    height: calc(100vh - var(--site-header-h) - 24px);
    height: calc(100dvh - var(--site-header-h) - 24px);
    min-height: 520px;
  }

  .dock {
    align-items: center;
    padding: 14px 6px;
    border-radius: 18px;
    background: #eef7fd;
    border-color: #dcebf6;
  }

  .dock-title {
    font-size: 14px;
  }

  .dock-title-full,
  .dock-date,
  .bonus,
  .tips,
  .badge-name {
    display: none;
  }

  .dock-title-short {
    display: inline;
  }

  .badges {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-top: 12px;
    width: 100%;
  }

  .badge {
    gap: 4px;
    padding: 6px 0;
    background: transparent;
  }

  .badge-ico {
    width: 58px;
    height: 58px;
    background: #fff;
  }

  .badge-count {
    font-size: 16px;
  }

  .panel {
    border-radius: 18px;
  }

  .panel-head {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 14px 12px 0;
  }

  .panel-title {
    font-size: 18px;
  }

  .search {
    max-width: none;
    height: 38px;
  }

  .chips {
    padding: 10px 12px 8px;
  }

  .chip {
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
  }

  .list {
    padding: 2px 12px 12px;
  }

  .cards {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .card {
    flex-direction: row;
    border-radius: 14px;
  }

  .cover {
    flex: none;
    width: 30%;
    aspect-ratio: auto;
  }

  .cover-no {
    left: 6px;
    top: 6px;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    font-size: 12px;
  }

  .info {
    min-width: 0;
    padding: 10px;
  }

  .name {
    font-size: 15px;
  }

  .area {
    font-size: 12px;
  }

  /* 窄手機上四格加起來可能比右欄還寬，改成跟著寬度等分縮，最大 36px */
  .slots {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 36px));
    gap: 6px;
    padding-top: 8px;
  }

  .slot {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }

  .slot-x {
    width: 16px;
    height: 16px;
    font-size: 12px;
  }

  .submit-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 10px 12px 12px;
  }

  .submit-bonus {
    display: block;
    margin: 0;
    color: var(--site-orange-deep);
    font-size: 12px;
    line-height: 1.4;
    text-align: center;
  }

  .submit-info {
    justify-content: center;
    font-size: 13px;
  }

  .submit-info b {
    font-size: 20px;
  }

  .submit-btn {
    min-width: 0;
    height: 46px;
    font-size: 16px;
  }

  .dlg-panel {
    padding: 28px 20px 24px;
  }

  .dlg-art {
    width: 108px;
    height: 108px;
  }

  .dlg-art img {
    width: 92px;
    height: 92px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .badge,
  .card,
  .slot,
  .submit-btn,
  .toast-enter-active,
  .toast-leave-active,
  .dlg-enter-active,
  .dlg-leave-active,
  .dlg-enter-active .dlg-panel,
  .dlg-leave-active .dlg-panel {
    transition: none;
  }

  .slot.is-placed {
    animation: none;
  }

  .badge:hover:not(.is-empty),
  .card.is-over,
  .slot.is-empty:hover,
  .slot.is-target,
  .submit-btn:hover:not(:disabled) {
    transform: none;
  }
}
</style>
